import React, { FunctionComponent, useEffect, useState } from 'react';

import Loading from '@justgiving/loading';
import useEventListener from '@use-it/event-listener';

import { GetStravaSettingsQuery } from '../../../types/__generated__';
import { PageMetaContext } from '../../context/PageMetaContext';
import useFitnessConnection from '../../hooks/fitness/useFitnessConnection';
import useStravaAuth from '../../hooks/fitness/useStravaAuth';
import { useApp } from '../../hooks/useApp';
import useFullPageQuery from '../../hooks/useFullPageQuery';
import { useModalToggle } from '../../hooks/useModalToggle';
import { useNotNullableContext } from '../../hooks/useNotNullableContext';
import Modal from '../Modal/Modal';
import StravaConnectBar from '../StravaConnectBar/StravaConnectBar';
import { StravaModal } from '../StravaSettingsModals/StravaModal';
import getStravaSettings from '../StravaSettingsModals/StravaSettingsModal/StravaSettingsModal.gql';

const VIEWS_UNTIL_COLLAPSED = 3;

const useIsStravaBarExpanded = ({
  isConnected,
  hasSettings,
  pageShortName,
}: {
  isConnected: boolean;
  hasSettings: boolean;
  pageShortName: string;
}) => {
  const state = isConnected && hasSettings ? 'connect' : 'disconnect';

  const isExpanded = localStorage.getItem(
    `strava.CTAisExpanded-${pageShortName}`,
  );

  const viewCount =
    Number(localStorage.getItem(`strava.${state}ViewCount-${pageShortName}`)) ??
    0;

  useEventListener('unload', () => {
    if (viewCount <= VIEWS_UNTIL_COLLAPSED) {
      localStorage.setItem(
        `strava.${state}ViewCount-${pageShortName}`,
        String(viewCount + 1),
      );
    }
  });

  if (isExpanded) {
    return JSON.parse(isExpanded);
  }
  return VIEWS_UNTIL_COLLAPSED > viewCount;
};

const Strava: FunctionComponent = () => {
  const { data: settingsData, loading } = useFullPageQuery<
    GetStravaSettingsQuery
  >(getStravaSettings, {
    context: {
      credentials: 'include',
    },
  });

  if (loading) {
    return <Loading />;
  }

  const isConnected = Boolean(
    settingsData?.viewer?.thirdPartyApplications?.strava?.isConnected,
  );

  const isPaused = Boolean(
    isConnected &&
      settingsData?.page?.strava?.settings?.subscribedActivities?.length === 0,
  );

  const hasSettings = Boolean(settingsData?.page?.strava?.settings);

  return (
    <StravaConnection
      isConnected={isConnected}
      hasSettings={hasSettings}
      isPaused={isPaused}
    />
  );
};

const StravaConnection: FunctionComponent<{
  isConnected: boolean;
  hasSettings: boolean;
  isPaused: boolean;
}> = ({ isConnected, hasSettings, isPaused }) => {
  const { showToast, hideToast, startLoading, stopLoading } = useApp();
  const { currentUrl, pageShortName } = useNotNullableContext(PageMetaContext);
  const { status, error } = useFitnessConnection(isConnected);
  const stravaAuthCallback = useStravaAuth(currentUrl);
  const isExpanded = useIsStravaBarExpanded({
    isConnected,
    hasSettings,
    pageShortName,
  });
  const [isOpen, setIsOpen] = useState<boolean>(isExpanded);
  const hasCookieConsent = document.cookie.includes('JgCookieNotice');
  const { isOpen: isModalOpen, setOpen: setModalOpen } = useModalToggle();

  useEffect(() => {
    if (status === 'connecting') {
      startLoading();
    } else if (status === 'success') {
      stopLoading();
      localStorage.setItem('strava.initialFormSave', String(1));
      setModalOpen(true);
    } else if (status === 'failed' && error) {
      stopLoading();
      showToast({
        message: error,
        type: 'warning',
      });
    }

    return () => {
      localStorage.removeItem('strava.initialFormSave');
    };
  }, [status, showToast, stopLoading, setModalOpen, startLoading, error]);

  const onToggle = () => {
    localStorage.setItem(
      `strava.CTAisExpanded-${pageShortName}`,
      JSON.stringify(!isOpen),
    );
    setIsOpen(!isOpen);
  };

  return (
    <>
      <StravaConnectBar
        onStravaAuth={stravaAuthCallback}
        onModalOpen={() => {
          hideToast();
          setModalOpen(true);
        }}
        hasSettings={hasSettings}
        isConnected={isConnected}
        isPaused={isPaused}
        isOpen={isOpen}
        onToggle={onToggle}
      />
      {isModalOpen && (
        <Modal
          onCloseRequest={() => setModalOpen(false)}
          scrollIsolation={hasCookieConsent}
        >
          <StravaModal
            connectionStatus={status}
            onClose={() => {
              setModalOpen(false);
            }}
          />
        </Modal>
      )}
    </>
  );
};

export default Strava;
